import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "omeupt_admin_token";

const AuthContext = createContext({
  authed: false,
  ready: false,
  login: async () => {},
  logout: () => {},
  error: "",
});

// Axios interceptor — anexa o Bearer token a TODAS as chamadas /api
let installed = false;
function installInterceptor() {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    const url = (config.url || "").toString();
    if (url.includes("/api/")) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        const url = (err.config?.url || "").toString();
        // Não despeja na rota de login pública
        if (!url.endsWith("/auth/login") && !url.endsWith("/leads") && !url.includes("/studio/photos")) {
          localStorage.removeItem(TOKEN_KEY);
          window.dispatchEvent(new Event("omeupt:auth-expired"));
        }
      }
      return Promise.reject(err);
    }
  );
}

export function AuthProvider({ children }) {
  const [authed, setAuthed] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  installInterceptor();

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      setReady(true);
      return;
    }
    try {
      await axios.get(`${API}/auth/me`);
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    checkSession();
    const onExpire = () => setAuthed(false);
    window.addEventListener("omeupt:auth-expired", onExpire);
    return () => window.removeEventListener("omeupt:auth-expired", onExpire);
  }, [checkSession]);

  const login = async (password) => {
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      const token = res.data?.token;
      if (!token) throw new Error("Resposta inválida");
      localStorage.setItem(TOKEN_KEY, token);
      setAuthed(true);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Falha no login. Tenta novamente.");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return (
    <AuthContext.Provider value={{ authed, ready, login, logout, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
